import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  TextField,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Alert,
  Autocomplete,
  FormHelperText,
} from '@mui/material';
import apiService from '../services/api';
import { toast } from 'react-toastify';
import { SendNotificationParams } from '../types/notificationSend.type';

const ROLE_OPTIONS = ['EXHIBITOR', 'DELEGATE', 'VISITOR'];
const ROUTE_OPTIONS = ['/events', '/agenda', '/dashboard'];

const SendNotification: React.FC = () => {
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [targetType, setTargetType] = useState<'all' | 'role'>('all');
  const [roles, setRoles] = useState<string[]>([]);
  const [routePath, setRoutePath] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [errors, setErrors] = useState<{
    title?: string;
    message?: string;
    roles?: string;
    routePath?: string;
  }>({});

  const handleSubmit = async () => {
    const newErrors: typeof errors = {};

    if (!title.trim()) {
      newErrors.title = 'Notification Title is required';
    }

    if (!message.trim()) {
      newErrors.message = 'Message is required';
    }

    if (targetType === 'role') {
      if (roles.length === 0) {
        newErrors.roles = 'Select at least one role';
      }
    }

    if (!routePath) {
      newErrors.routePath = 'Please select a route path';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) return;

    // const payload: SendNotificationParams = {
    //     title,
    //     message,
    //     targetType,
    //     roles: targetType === 'all' ? [] : roles,
    //     routePath,
    // };
    const payload: SendNotificationParams = {
      title,
      message,
      targetType,
      routePath,
    };

    if (targetType === 'role') {
      payload.roles = roles;
    }

    console.log('payload', payload);

    try {
      setLoading(true);
      await apiService.sendNotification(payload);
      toast.success('Notification sent successfully');
      setTitle('');
      setMessage('');
      setRoles([]);
      setTargetType('all');
      setRoutePath('');
      setErrors({});
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Failed to send notification');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f9fafb', minHeight: '100vh' }}>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Card
        sx={{
          maxWidth: 700,
          mx: 'auto',
          p: 4,
          borderRadius: 2,
          border: '1px solid #e5e7eb',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06)',
        }}
      >
        {/* Target Type */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 3,
          }}
        >
          <Box>
            <Typography
              sx={{ fontSize: '0.875rem', fontWeight: 600, color: '#111827' }}
            >
              Target Audience
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: '#6b7280' }}>
              Choose who should receive this notification
            </Typography>
          </Box>

          <ToggleButtonGroup
            value={targetType}
            exclusive
            onChange={(_, value) => value && setTargetType(value)}
            size="small"
            sx={{
              bgcolor: '#ffffff',
              borderRadius: 1,
              '& .MuiToggleButton-root': {
                textTransform: 'none',
                px: 2.5,
                fontWeight: 500,
              },
            }}
          >
            <ToggleButton value="all">All Users</ToggleButton>
            <ToggleButton value="role">Specific Role</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Stack spacing={3}>
          {/* Title */}
          <TextField
            label="Notification Title"
            value={title}
            onChange={e => {
              setTitle(e.target.value);
              setErrors(prev => ({ ...prev, title: undefined }));
            }}
            fullWidth
            error={!!errors.title}
            helperText={errors.title}
          />

          {/* Message */}
          <TextField
            label="Message"
            value={message}
            onChange={e => {
              setMessage(e.target.value);
              setErrors(prev => ({ ...prev, message: undefined }));
            }}
            multiline
            rows={4}
            fullWidth
            error={!!errors.message}
            helperText={errors.message}
          />

          {/* Roles */}
          {targetType != 'all' && (
            <>
              {/* Roles Autocomplete */}
              <Autocomplete
                multiple
                options={ROLE_OPTIONS}
                value={roles}
                onChange={(_, newValue) => {
                  setRoles(newValue);
                  setErrors(prev => ({ ...prev, roles: undefined }));
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label="Roles"
                    placeholder="Select roles"
                    fullWidth
                    error={!!errors.roles}
                    helperText={errors.roles}
                  />
                )}
              />
            </>
          )}

          {/* Route Path */}
          <FormControl fullWidth error={!!errors.routePath}>
            <InputLabel>Route Path</InputLabel>
            <Select
              value={routePath}
              label="Route Path"
              onChange={e => {
                setRoutePath(e.target.value);
                setErrors(prev => ({ ...prev, routePath: undefined }));
              }}
            >
              {ROUTE_OPTIONS.map(route => (
                <MenuItem key={route} value={route}>
                  {route}
                </MenuItem>
              ))}
            </Select>
            {errors.routePath && (
              <FormHelperText>{errors.routePath}</FormHelperText>
            )}
          </FormControl>

          {/* Submit */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={loading}
              sx={{
                bgcolor: '#3b82f6',
                textTransform: 'none',
                px: 3,
                '&:hover': { bgcolor: '#2563eb' },
              }}
            >
              Send Notification
            </Button>
          </Box>
        </Stack>
      </Card>
    </Box>
  );
};

export default SendNotification;
